import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    Input,
    OnDestroy,
    OnInit,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { MatLine } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { BxStickyActionBarModule } from '@buildxact/ui-components/bx-sticky-action-bar';
import { BxTableHeaderModule } from '@buildxact/ui-components/bx-table-header';
import { DatatableComponent, NgxDatatableModule, SelectionType, TableColumn } from '@siemens/ngx-datatable';
import { firstValueFrom } from 'rxjs';
import { delay, finalize, first } from 'rxjs/operators';
import { EmailMessageReferenceInput, EmailTransactionStatusEnum } from '../../../../../../graphql';
import { SingleClickModule } from '../../../../../core/directives/single-click.directive';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { DialogService } from '../../../../../core/services/dialog.service';
import { LocalStorageService } from '../../../../../core/services/local-storage.service';
import { NotificationService } from '../../../../../core/services/notification.service';
import { PermissionService } from '../../../../../core/services/permission';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { LayoutService } from '../../../../layout/layout.service';
import { MessagesService } from '../../../messages.service';
import { EmailApisService } from '../../domain/apis/email.apis.service';
import { EmailTrackingStatusChangedSignalREventModel } from '../../domain/models/email-tracking-status-changed-event-model';
import { EmailListItemModel, EmailResult } from '../../domain/models/email.models';
import { EmailTrackingStatusComponent } from '../../email-status/email-tracking-status/email-tracking-status.component';

interface EmailListWidthParams {
    date: number;
    to: number;
    subject: number;
    status: number;
    sentBy: number;
}

interface EmailListTableSelectEvent {
    selected: Array<EmailListItemModel>;
}

@Component({
    selector: 'app-email-list',
    imports: [
        BxTableHeaderModule,
        NgxDatatableModule,
        CommonModule,
        BxStickyActionBarModule,
        MatListModule,
        MatLine,
        EmailTrackingStatusComponent,
        SingleClickModule,
    ],
    templateUrl: './email-list.component.html',
    styleUrl: './email-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailListComponent implements OnInit, AfterViewInit, OnDestroy {
    rows = signal<EmailListItemModel[]>([]);
    selectedRows = signal<EmailListItemModel[]>([]);
    search = '';
    isLoading = signal<boolean>(false);
    isLoaded = signal<boolean>(false);
    hasUserScrolled = signal<boolean>(false);
    endOfList = false;
    continuationToken?: string | null = null;
    emailListWidths: EmailListWidthParams = {} as EmailListWidthParams;
    canDeleteEmails = false;
    readonly headerHeight = 50;
    readonly rowHeight = 40;
    readonly pageLimit = 30;
    readonly emailListTableWidthsStorageKey = 'email-list-table';
    selectionType = SelectionType;

    #onEmailTrackingStatusChangedProxy = this.onEmailTrackingStatusChanged.bind(this);
    readonly emailTrackingStatusChangedEventName = 'EmailTrackingStatusChanged';

    @ViewChild('emailListTable') table!: DatatableComponent;

    showCreateNew = input<boolean>(true);
    createNewEnabled = input<boolean>(true);
    showSearch = input<boolean>(true);
    currentUserOnly = input<boolean>(false);
    title = input<string>('Email');
    newEmail = output<void>();
    newEmailPermission = input<string | undefined>(undefined);
    @Input() references: EmailMessageReferenceInput[] | undefined;

    #emailApisService = inject(EmailApisService);
    layoutService = inject(LayoutService);
    #localStorageService = inject(LocalStorageService);
    #elementRef = inject(ElementRef);
    #logger = inject(BxLogger);
    #messagesService = inject(MessagesService);
    #dialogService = inject(DialogService);
    permissionService = inject(PermissionService);
    #notificationService = inject(NotificationService);
    #sharedData = inject(SharedDataService);
    #bxUnverifiedAlertService = inject(BxUnverifiedAlertService);

    ngOnInit() {
        if (this.#localStorageService.getObject(this.emailListTableWidthsStorageKey)) {
            this.emailListWidths = this.#localStorageService.getObject(this.emailListTableWidthsStorageKey);
        }
        this.canDeleteEmails = this.permissionService.isAvailable('messages.maintain-emails.delete-emails');
        this.#notificationService.connection.on(this.emailTrackingStatusChangedEventName, this.#onEmailTrackingStatusChangedProxy);
    }

    ngAfterViewInit() {
        this.getEmails(true);
    }

    ngOnDestroy() {
        this.#notificationService.connection.off(this.emailTrackingStatusChangedEventName, this.#onEmailTrackingStatusChangedProxy);
    }

    onEmailTrackingStatusChanged(message: EmailTrackingStatusChangedSignalREventModel) {
        const messageRow = this.rows().find((r) => r.id === message.id);

        // if the email is in the list, we update the status (no need to refresh the list)
        if (messageRow) {
            this.rows.update((currentItems) => {
                return currentItems.map((item) => {
                    if (item.id !== message.id) {
                        return item;
                    }
                    return {
                        ...item,
                        hasError: message.hasError,
                        overallStatus: message.overallStatus.toUpperCase() as EmailTransactionStatusEnum,
                        trackingStatus: item.trackingStatus.map((ts) => ({
                            ...ts,
                            value: {
                                status:
                                    (message.trackingStatus[ts.key]?.Status?.toUpperCase() as EmailTransactionStatusEnum) ||
                                    ts.value.status,
                            },
                        })),
                    };
                });
            });
        }
        // if the user has scrolled or searched, we don't want to refresh the list, so any other scenario is not handled
        else if (!this.hasUserScrolled() && this.search === '') {
            this.getEmails(true);
        }
    }

    createNewEmail() {
        this.newEmail.emit();
    }

    refreshEmailList() {
        this.getEmails(true);
    }

    getEmails(reset = false, previousTotalFetched: number = 0, delayMs: number = 0) {
        this.isLoading.set(true);
        if (reset) {
            this.hasUserScrolled.set(false);
            this.rows.set([]);
            this.continuationToken = null;
            this.endOfList = true;
        }

        this.#emailApisService
            .listEmails({
                continuationToken: this.continuationToken,
                pageSize: this.pageLimit,
                searchText: this.search,
                references: this.references,
            })
            .pipe(
                delay(delayMs),
                first(),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe((result) => {
                if (result.error !== undefined) {
                    this.#logger.error('An error occurred trying to load emails.');
                } else {
                    let existingRows = reset ? [] : this.rows();
                    this.continuationToken = result.data.listEmails.continuationToken ?? null;
                    const newRows = result.data.listEmails.items?.filter((x): x is NonNullable<typeof x> => !!x) ?? [];
                    this.rows.set([...existingRows, ...newRows]);
                    this.endOfList = this.continuationToken === null;
                    this.isLoaded.set(true);
                    // Email api can return less rows than expected with a valid continuation token
                    // This is due to several factors such as result set size, partition limits and index page sizes.
                    // So we continue to load more until we reach the total requested with the pageLimit
                    const totalFetched = previousTotalFetched + newRows.length;
                    // A valid continuation token indicates there are more rows in the query result set
                    if (this.continuationToken && totalFetched < this.pageLimit) {
                        // Call is delayed to prevent Http 429 from the api
                        this.getEmails(false, totalFetched, 500);
                    }
                }
            });
    }

    setSearch(event: string) {
        this.search = event;
        this.getEmails(true);
    }

    onScroll(e: MouseEvent) {
        // total height of all rows in the viewport
        const viewHeight = this.#elementRef.nativeElement.getBoundingClientRect().height - this.headerHeight;
        // check if we scrolled to the bottom of the viewport
        if (!this.isLoading() && e.offsetY + viewHeight >= this.rows().length * this.rowHeight - 300) {
            if (!this.endOfList) {
                this.getEmails(false);
                this.hasUserScrolled.set(true);
            }
        }
    }

    onResize(e: { column: TableColumn; newValue: number | undefined }) {
        if (!e.newValue) return;
        switch (e.column.name) {
            case 'Sent':
                this.emailListWidths.date = e.newValue;
                break;
            case 'Recipients':
                this.emailListWidths.to = e.newValue;
                break;
            case 'Subject':
                this.emailListWidths.subject = e.newValue;
                break;
            case 'Status':
                this.emailListWidths.status = e.newValue;
                break;
            case 'Sent By':
                this.emailListWidths.sentBy = e.newValue;
                break;
        }
        this.#localStorageService.saveObject(this.emailListTableWidthsStorageKey, this.emailListWidths);
    }

    spaceDelimiter(val: string) {
        return val.replace(/,/g, ', ');
    }

    async viewEmail(row: EmailListItemModel) {
        const emailResult = await firstValueFrom(this.#emailApisService.getEmail(row.id));
        const email = emailResult.data.email;
        try {
            const result = await this.#messagesService.viewEmail(email);
            if (result && result === 'resend') {
                this.resendEmailMessage(email);
            }
        } catch (_) {}
    }

    async deleteEmail(row: EmailListItemModel) {
        try {
            await this.#dialogService.show({
                title: 'Delete email?',
                text: `The email '${row.subject}' will be deleted.`,
            });
            // if not cancelled the following will run
            const deleteResult = await firstValueFrom(this.#emailApisService.deleteEmails([row.id]));
            if (deleteResult.errors !== undefined && deleteResult.errors.length) {
                this.#logger.error('An error occurred when trying to delete the email');
            } else {
                this.rows.set(this.rows().filter((e) => e !== row));
                this.#logger.success('Email deleted successfully');
            }
        } catch (_) {}
    }

    async resendEmail(row: EmailListItemModel) {
        const emailResult = await firstValueFrom(this.#emailApisService.getEmail(row.id));
        const email = emailResult.data.email;
        this.resendEmailMessage(email);
    }

    resendEmailMessage(email: EmailResult) {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }

        const emailConfig: any = {
            // @ts-ignore TS7006
            To: email.to.map((r) => {
                return {
                    email: r,
                    name: '',
                };
            }),
            Subject: email.subject,
            Body: email.body,
            References: this.references,
        };

        if (email.attachments) {
            // @ts-ignore TS7006
            emailConfig.StorageAttachments = email.attachments.map((a) => {
                return a.url;
            });
        }
        this.#messagesService.sendEmail(emailConfig);
    }

    onSelect(e: EmailListTableSelectEvent) {
        this.selectedRows.set([...e.selected]);
    }

    clearSelectedRows() {
        this.selectedRows.set([]);
    }

    async deleteSelectedEmails() {
        try {
            await this.#dialogService.show({
                title: 'Delete selected emails?',
                text: `${this.selectedRows().length} email${this.selectedRows().length > 1 ? 's' : ''} will be deleted.`,
            });
            await this.deleteEmails(this.selectedRows());
            this.clearSelectedRows();
        } catch (_) {}
    }

    async deleteEmails(rows: EmailListItemModel[]) {
        const ids = rows.map((r) => r.id);
        const deleteResult = await firstValueFrom(this.#emailApisService.deleteEmails(ids));
        if (deleteResult.errors?.length) {
            this.#logger.error(`An error occurred when trying to delete ${ids.length > 1 ? 'the email' : 'email messages'}`);
        } else {
            this.rows.set(this.rows().filter((e) => !ids.includes(e.id)));
            this.#logger.success(`Email${ids.length > 1 ? 's' : ''} deleted successfully`);
        }
    }
}
